/**
 * HKOS settings.
 *
 * ⚠️ HOUSE RULES VARY. Hong Kong Old Style is a family of rulesets, not one
 * fixed standard — fan values for a few patterns, and especially the fan→points
 * table, differ between clubs. Everything contentious is a setting with a
 * documented default rather than a hardcoded constant. Confirm the values
 * below against the house rules of the venue before going live.
 *
 * Two scoring modes, chosen fresh per game (see `ScoringMode`):
 *  - 'serious': today's traditional model — exponential fan→points, zero-sum
 *    settlement (losers go negative). No real money is ever involved; this is
 *    a competitive points-tracking style, closer to tournament mahjong scoring
 *    than to a wager.
 *  - 'fun': only the round winner ever gains points; nobody else's total can
 *    go negative, including on a draw. For a social, no-stakes game centre
 *    where a flexible, exciting scoring curve matters more than strict payout
 *    fairness between players.
 */

export interface FanValues {
  allSequences: number;
  allPungs: number;
  /**
   * Seven ordinary pairs (any suits/ranks). Stacks with Full Flush/Half
   * Flush/All Honours like any other pattern — see CLAUDE.md. Never
   * includes a kong: the shape requires all 7 pairs concealed, and a kong
   * claim is explicitly rejected at validation (confirmed 2026-09-10) rather
   * than silently falling through to a different, worse reading. Does NOT
   * stack with Fully Concealed Hand (`concealedHand`) — reversed 2026-09-22,
   * see decisions-log.md.
   */
  sevenPairs: number;
  halfFlush: number;
  fullFlush: number;
  concealedHand: number;
  selfDraw: number;
  /**
   * Every tile is a wind or dragon. Two uses: (1) as its own pattern (the
   * standard 4-sets-and-a-pair shape) it is one of the four fixed-value limit
   * hands — see `detectLimitHands` in patterns.ts and `orphans`/`nineGates`/
   * `selfTriplets` below: it scores exactly this many fan and nothing stacks
   * on it (confirmed 2026-09-19, following the Wikipedia page's 10 faan);
   * (2) inside Seven Pairs (`detectSevenPairs`), for the rare case where all
   * 7 pairs are the 7 honour tiles, it stacks like any ordinary pattern — a
   * deliberate, separate decision from 2026-09-10.
   */
  allHonours: number;
  /**
   * Orphans, Nine Gates and Self Triplets — with All Honours, the four hands
   * Wikipedia's Hong Kong scoring page lists at 10 faan and calls limit hands
   * that take no additional faan. Confirmed 2026-09-19: a matching hand scores
   * exactly this value ALONE (nothing stacks), and it is still bounded by the
   * mode's fan cap like any other hand. Great Winds, All Kongs and Thirteen
   * Orphans are the 13-faan tier instead and pay the cap itself. These fields
   * can be missing on a game saved before they existed — read them through
   * `fixedLimitFan` in patterns.ts, never directly.
   */
  selfTriplets: number;
  orphans: number;
  nineGates: number;
  /**
   * Added 2026-09-11: every tile is a terminal (1/9) OR an honour, with at
   * least one of each — a genuine mix. Orphans and All Honours (the two
   * "pure" cases) are forced limit hands handled in `detectLimitHands` and
   * never reach the code path this pattern lives in, so there's no overlap
   * to worry about. This tile restriction makes a chow impossible, so every
   * hand that qualifies is also All Pungs — this is the pattern's OWN fan
   * only, and is meant to stack additively with `allPungs` (1 + 3 = 4
   * total), unlike the non-stacking treatment given to Great/Small
   * Dragons/Small Winds. Still suppressed by `suppressGenericStructure`
   * when Small Winds or Great/Small Dragons ALSO fires (e.g. 3 wind pungs +
   * a terminal pung + a wind pair) — the more specific ordinary pattern
   * wins there.
   */
  mixedOrphans: number;
  /** A single pung/kong of one dragon tile. NOT awarded on top of Great/Small Dragons — see those fields. */
  dragonPung: number;
  /**
   * A pung/kong of all three dragons. Formerly a limit hand ("Big Three
   * Dragons"/"Great Dragons"); now an ordinary fixed-fan pattern. Confirmed
   * 2026-09-10: this is the hand's ENTIRE structural/honour-composition fan
   * — it replaces the 3× `dragonPung` those same melds would otherwise earn
   * (reversing the original 2026-09-09 design), and ALSO replaces All Pungs
   * if the 4th meld and pair happen to also qualify for that (e.g. 3 dragon
   * pungs + 1 more pung + a pair). Doesn't touch flush (Half/Full Flush
   * still stack normally, same as everywhere else).
   */
  greatDragons: number;
  /**
   * 2 dragon pungs/kongs + a pair of the 3rd dragon. Confirmed 2026-09-10:
   * same non-stacking treatment as Great Dragons — replaces `dragonPung`
   * AND All Pungs for the same melds, never stacks with them. Default value
   * (5) already bakes in what used to be the separately-stacked total
   * (3 own + 2×1 dragonPung).
   */
  smallDragons: number;
  /**
   * 3 wind pungs/kongs + a pair of the 4th wind. This is the pattern's own
   * fan, independent of any seat/prevailing wind pung bonus (those are
   * conditional toggles and apply on top when relevant). The 4th, non-wind
   * meld is usually from a numbered suit, which naturally also earns Half
   * Flush — so a Small Winds hand is worth at least smallWinds + halfFlush
   * fan in practice at defaults (6 + 3 = 9). Confirmed 2026-09-10: when the
   * 4th meld happens to ALSO be a pung (making every set a triplet), Small
   * Winds does NOT additionally stack with All Pungs — it replaces that
   * generic structural credit, same non-stacking treatment as Great/Small
   * Dragons. Half Flush is unaffected (a different dimension — flush, not
   * triplet composition). NOTE: if the 4th meld is instead an honour
   * (making every tile a wind/dragon), the hand is All Honours — a forced
   * limit hand — which wins outright; see `detectLimitHands`.
   */
  smallWinds: number;
  /**
   * Ordinary fixed fan for a chained kong-on-kong self-draw win — see the
   * doc comment on `WinContext.winDoubleKong`. Implies self-draw, same as
   * `kongReplacement`, but scored separately rather than stacked on top of
   * it (the two are alternative descriptions of the same win, not additive).
   */
  winDoubleKong: number;
  seatWindPung: number;
  prevailingWindPung: number;
  flower: number;
  /** Bonus for a flower/season whose printed number matches your seat wind. Stacks per matching tile. */
  seatFlowerBonus: number;
  /**
   * Holding all 4 flower tiles (F1-F4). Since one of the 4 is always your own
   * seat's flower, this deliberately does NOT stack with `seatFlowerBonus`
   * for that specific tile — see the suppression in `detectPatterns`.
   */
  allFlowers: number;
  /** Holding all 4 season tiles (S1-S4). Same non-stacking treatment as `allFlowers`, for the matching season tile. */
  allSeasons: number;
  /** Bonus for holding zero flower/season tiles at all. Mutually exclusive with `flower` — never both on the same hand. */
  noFlowerBonus: number;
  robbingKong: number;
  kongReplacement: number;
  lastWallTile: number;
  /** Won by claiming the very last discard of the game, before the wall runs out — the discard-side counterpart to `lastWallTile`. Added 2026-09-22. */
  lastDiscard: number;
}

/** Fan-detection toggles. Meaningful in both modes — pattern detection is mode-independent. */
export interface HkosToggles {
  seatWindPung: boolean;
  prevailingWindPung: boolean;
  flowerBonus: boolean;
  seatFlowerBonus: boolean;
  allFlowers: boolean;
  allSeasons: boolean;
  noFlowerBonus: boolean;
  robbingKong: boolean;
  kongReplacement: boolean;
  lastWallTile: boolean;
  lastDiscard: boolean;
  heavenlyHand: boolean;
  earthlyHand: boolean;
  winDoubleKong: boolean;
  /**
   * The Hand helper's "Suggestions" (Indra, 2026-10-04) — NOT a fan toggle, it never touches scoring. On by default; chosen with the other
   * House Rules before a game starts and locked once it has, so the whole table can see whether it was on. Never on in a leaderboard game
   * (see `handHelperOn`). A game saved before it existed has no value, which counts as on.
   */
  handHelper: boolean;
}

export type DiscardPayment = 'discarderPaysAll' | 'discarderPaysDouble';
export type ScoringMode = 'serious' | 'fun';

/**
 * Settlement-mechanic settings that only mean something inside Serious mode's
 * zero-sum, multi-payer scheme. Nested here (rather than left at the top
 * level and ignored in Fun mode) so a Fun-mode settlement can't accidentally
 * read a concept — a discarder to charge, a dealer to double against — that
 * doesn't exist in a winner-only model.
 */
export interface SeriousModeSettings {
  /**
   * Which of the two fan→points curves below is actually used. Confirmed
   * 2026-09-29: substitutional, not additive — a game uses `payoutBase`
   * (exponential) OR `multiplier` (linear), never both, so the club can pick
   * the simpler linear curve without the exponential's `payoutBase` field
   * secretly still being live. Defaults to 'exponential', matching every
   * game already scored before this setting existed — see
   * `activePayoutMethod` below, which is how this field must always be
   * read (never directly) so an old saved game without it still falls back
   * correctly instead of reading `undefined`.
   */
  payoutMethod: 'exponential' | 'multiplier';
  /** fan→points exponent base (base^fan). Club convention: 1.5–2. Only used when payoutMethod is 'exponential'. */
  payoutBase: number;
  /**
   * fan→points linear multiplier (fan × multiplier) — the same idea as Fun
   * mode's own `multiplier`, offered as a simpler alternative to the
   * exponential curve above. Only used when payoutMethod is 'multiplier'.
   * `baseUnit` below still applies on top either way, same as it always has.
   */
  multiplier: number;
  /** Fan cap for this mode — also the fan value of any declared limit hand while in this mode. */
  maxFanCap: number;
  /** Multiplier applied to every base value. Set the table stake here. */
  baseUnit: number;
  discardPayment: DiscardPayment;
  dealerDoublePayout: boolean;
  tenpaiPaymentOnDraw: boolean;
  /** Points each non-tenpai player pays each tenpai player on a draw. */
  tenpaiPaymentUnits: number;
  /** Minimum fan required for a hand to be a legal win in this mode. Editable, not fixed. */
  minimumFan: number;
}

export interface FunModeSettings {
  /** Applied to raw (already-capped) fan. 1 = the fan number itself. */
  multiplier: number;
  /** Fan cap for this mode — independent of serious.maxFanCap. */
  maxFanCap: number;
  /**
   * Minimum fan required for a hand to be a legal win in this mode. Defaults
   * to 0 so Fun mode games can allow "chicken hand" (no-fan) wins without
   * restriction, per house-rule preference — independent of serious.minimumFan.
   */
  minimumFan: number;
}

export interface HkosSettings {
  mode: ScoringMode;
  fan: FanValues;
  toggles: HkosToggles;
  serious: SeriousModeSettings;
  fun: FunModeSettings;
}

/** The fan cap that applies right now, given settings.mode. */
export function activeFanCap(settings: HkosSettings): number {
  return settings.mode === 'fun' ? settings.fun.maxFanCap : settings.serious.maxFanCap;
}

/** The minimum fan that applies right now, given settings.mode. */
export function activeMinimumFan(settings: HkosSettings): number {
  return settings.mode === 'fun' ? settings.fun.minimumFan : settings.serious.minimumFan;
}

/**
 * Serious mode's fan→points curve, defaulting to 'exponential' for a game
 * saved before this setting existed (see `SeriousModeSettings.payoutMethod`)
 * — read through this, never `settings.serious.payoutMethod` directly.
 */
export function activePayoutMethod(settings: HkosSettings): 'exponential' | 'multiplier' {
  return settings.serious.payoutMethod ?? 'exponential';
}

const DEFAULT_FAN: FanValues = {
  allSequences: 1,
  allPungs: 3,
  // 4 fan, confirmed 2026-09-22 (reversed from 7 — the earlier "parity with
  // fullFlush" reasoning cited MCR's point table, which doesn't govern this
  // HKOS ruleset; corrected after checking against several HKOS references).
  sevenPairs: 4,
  halfFlush: 3,
  fullFlush: 7,
  concealedHand: 1,
  selfDraw: 1,
  // Only used by the Seven Pairs "all 7 pairs are honours" case now — see
  // this field's own doc comment on the FanValues interface above.
  allHonours: 10,
  selfTriplets: 10,
  orphans: 10,
  nineGates: 10,
  mixedOrphans: 1,
  dragonPung: 1,
  greatDragons: 8,
  smallDragons: 5,
  smallWinds: 6,
  winDoubleKong: 8,
  seatWindPung: 1,
  prevailingWindPung: 1,
  flower: 1,
  seatFlowerBonus: 1,
  allFlowers: 2,
  allSeasons: 2,
  noFlowerBonus: 1,
  robbingKong: 1,
  kongReplacement: 1,
  lastWallTile: 1,
  lastDiscard: 1,
};

export const DEFAULT_SETTINGS: HkosSettings = {
  mode: 'serious',
  fan: DEFAULT_FAN,
  toggles: {
    seatWindPung: false,
    prevailingWindPung: false,
    flowerBonus: false,
    seatFlowerBonus: false,
    allFlowers: false,
    allSeasons: false,
    noFlowerBonus: false,
    robbingKong: false,
    kongReplacement: false,
    lastWallTile: false,
    lastDiscard: false,
    heavenlyHand: false,
    earthlyHand: false,
    winDoubleKong: false,
    handHelper: true,
  },
  serious: {
    payoutMethod: 'exponential',
    payoutBase: 2,
    multiplier: 1,
    maxFanCap: 13,
    baseUnit: 1,
    discardPayment: 'discarderPaysAll',
    dealerDoublePayout: true,
    tenpaiPaymentOnDraw: false,
    tenpaiPaymentUnits: 1,
    minimumFan: 1,
  },
  fun: {
    multiplier: 1,
    maxFanCap: 13,
    minimumFan: 0,
  },
};

/** Deep-merge a partial override onto the defaults. */
export function makeSettings(overrides: DeepPartial<HkosSettings> = {}): HkosSettings {
  return {
    ...DEFAULT_SETTINGS,
    ...overrides,
    fan: { ...DEFAULT_SETTINGS.fan, ...(overrides.fan ?? {}) },
    toggles: { ...DEFAULT_SETTINGS.toggles, ...(overrides.toggles ?? {}) },
    serious: { ...DEFAULT_SETTINGS.serious, ...(overrides.serious ?? {}) },
    fun: { ...DEFAULT_SETTINGS.fun, ...(overrides.fun ?? {}) },
  } as HkosSettings;
}

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? Partial<T[K]> : T[K];
};
